import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Trash2, NotebookPen, CalendarPlus } from 'lucide-react';
import { API } from '@/lib/api';
import { AgendarCitaDialog } from './AgendarCitaDialog';

const RESULTADOS_RAPIDOS = ['No le interesó', 'Necesita dinero'];

// Bitácora de seguimiento de un prospecto: notas con fecha (ej. "se le dijo
// que la gestora legal le llamaría"). Vive en su propia colección para no
// tocar el formulario general de edición de paciente. Se usa tanto en el
// diálogo rápido de la lista de Prospectos como en la pestaña del expediente.
export const SeguimientoPanel = ({ patient, onChange, onCitaAgendada }) => {
  const [notas, setNotas]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [nueva, setNueva]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [agendarOpen, setAgendarOpen] = useState(false);

  const fetchNotas = async () => {
    if (!patient) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/patients/${patient.id}/seguimiento`);
      setNotas(res.data);
    } catch { toast.error('Error al cargar el seguimiento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchNotas(); }, [patient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const agregarNota = async (texto) => {
    if (!texto.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/patients/${patient.id}/seguimiento`, { nota: texto.trim() });
      setNueva('');
      fetchNotas();
      onChange?.();
    } catch { toast.error('Error al guardar la nota'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/patients/${patient.id}/seguimiento/${id}`);
      setNotas(prev => prev.filter(n => n.id !== id));
      onChange?.();
    } catch { toast.error('Error al eliminar la nota'); }
  };

  const handleCitaAgendada = () => {
    agregarNota('Se le agendó una nueva cita');
    onCitaAgendada?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {RESULTADOS_RAPIDOS.map((texto) => (
          <Button key={texto} type="button" variant="outline" size="sm" disabled={saving} onClick={() => agregarNota(texto)}>
            {texto}
          </Button>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setAgendarOpen(true)}>
          <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
          Agendar cita
        </Button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); agregarNota(nueva); }} className="space-y-2">
        <Textarea
          rows={2}
          placeholder="Escribe otra nota de seguimiento..."
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <Button type="submit" size="sm" disabled={saving || !nueva.trim()}>
          Agregar nota
        </Button>
      </form>

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-6">Cargando...</p>
        ) : notas.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-muted-foreground">
            <NotebookPen className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">Aún no hay notas de seguimiento</p>
          </div>
        ) : (
          notas.map((n) => (
            <div key={n.id} className="flex items-start gap-2 p-3 rounded-lg border border-border/50">
              <div className="flex-1 min-w-0">
                <p className="text-sm">{n.nota}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(n.fecha).toLocaleString()}
                </p>
              </div>
              <Button
                variant="ghost" size="icon" className="h-7 w-7 shrink-0 hover:text-destructive"
                onClick={() => handleDelete(n.id)} aria-label="Eliminar nota"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>

      <AgendarCitaDialog
        patientId={patient?.id}
        patient={patient}
        open={agendarOpen}
        onOpenChange={setAgendarOpen}
        onCreated={handleCitaAgendada}
      />
    </div>
  );
};
